import { IGazeAdapter, GazePoint } from "./IGazeAdapter";
import { GAME_WIDTH, GAME_HEIGHT } from "../app/Config";
import { WebGazeAdapter } from "./WebGazeAdapter";

// MediaPipe globals (loaded via CDN script tags in index.html)
declare const FaceMesh: any;
declare const Camera: any;

/**
 * WebカメラとMediaPipe FaceMeshを使って虹彩位置をトラッキングし、
 * ゲームのカーソルを制御するアダプター。
 * カメラが使用不可の場合はマウス入力にフォールバック。
 */
export class CameraGazeAdapter implements IGazeAdapter {
  private point: GazePoint;
  private faceMesh: any = null;
  private camera: any = null;
  private videoEl: HTMLVideoElement | null = null;
  private fallback: WebGazeAdapter;
  private useFallback = false;

  // EMAスムージング（ジッター抑制）
  private smoothX: number;
  private smoothY: number;
  private readonly SMOOTH_ALPHA = 0.15;

  constructor(private scene: Phaser.Scene) {
    this.smoothX = GAME_WIDTH / 2;
    this.smoothY = GAME_HEIGHT / 2;
    this.point = { x: this.smoothX, y: this.smoothY };
    this.fallback = new WebGazeAdapter(scene);
  }

  start(): void {
    // MediaPipeが読み込まれているか確認
    if (typeof FaceMesh === "undefined" || typeof Camera === "undefined") {
      console.warn("[CameraGazeAdapter] MediaPipe未ロード → マウスフォールバック");
      this.useFallback = true;
      this.fallback.start();
      return;
    }

    // カメラプレビュー用videoエレメント（右下に小さく表示）
    this.videoEl = document.createElement("video");
    Object.assign(this.videoEl.style, {
      position: "fixed",
      bottom: "8px",
      right: "8px",
      width: "120px",
      height: "90px",
      borderRadius: "8px",
      opacity: "0.55",
      zIndex: "9999",
      transform: "scaleX(-1)", // ミラー表示
      pointerEvents: "none",
    });
    document.body.appendChild(this.videoEl);

    try {
      this.faceMesh = new FaceMesh({
        locateFile: (file: string) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@0.4/${file}`,
      });

      this.faceMesh.setOptions({
        maxNumFaces: 1,
        refineLandmarks: true, // 虹彩ランドマーク(468+)を有効化
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });

      this.faceMesh.onResults((results: any) => this.onResults(results));

      this.camera = new Camera(this.videoEl, {
        onFrame: async () => {
          if (this.videoEl && this.faceMesh) {
            await this.faceMesh.send({ image: this.videoEl });
          }
        },
        width: 320,
        height: 240,
      });

      this.camera.start().catch((err: Error) => {
        console.warn("[CameraGazeAdapter] カメラ起動失敗 → フォールバック:", err);
        this.switchToFallback();
      });
    } catch (err) {
      console.warn("[CameraGazeAdapter] 初期化失敗 → フォールバック:", err);
      this.switchToFallback();
    }
  }

  private onResults(results: any): void {
    if (
      !results.multiFaceLandmarks ||
      results.multiFaceLandmarks.length === 0
    ) {
      return;
    }

    const landmarks = results.multiFaceLandmarks[0];

    // 虹彩ランドマーク（refineLandmarks: true が必要）
    // 左虹彩中心: 468, 右虹彩中心: 473
    const leftIris = landmarks[468];
    const rightIris = landmarks[473];

    let normX: number;
    let normY: number;

    if (leftIris && rightIris) {
      // 両目の虹彩中心を平均して視点を算出
      normX = (leftIris.x + rightIris.x) / 2;
      normY = (leftIris.y + rightIris.y) / 2;
    } else {
      // 虹彩未検出時は鼻先（ランドマーク1）で代替
      const nose = landmarks[1];
      if (!nose) return;
      normX = nose.x;
      normY = nose.y;
    }

    // Webカメラ映像はX軸反転（鏡像）なのでX座標を反転
    const targetX = (1 - normX) * GAME_WIDTH;
    const targetY = normY * GAME_HEIGHT;

    // EMAスムージングでカーソルのジッターを抑制
    this.smoothX =
      this.SMOOTH_ALPHA * targetX + (1 - this.SMOOTH_ALPHA) * this.smoothX;
    this.smoothY =
      this.SMOOTH_ALPHA * targetY + (1 - this.SMOOTH_ALPHA) * this.smoothY;

    this.point = { x: this.smoothX, y: this.smoothY };
  }

  private switchToFallback(): void {
    this.useFallback = true;
    this.removeVideo();
    this.fallback.start();
  }

  private removeVideo(): void {
    if (this.videoEl && document.body.contains(this.videoEl)) {
      document.body.removeChild(this.videoEl);
      this.videoEl = null;
    }
  }

  stop(): void {
    if (this.useFallback) {
      this.fallback.stop();
      return;
    }
    this.camera?.stop();
    this.faceMesh?.close();
    this.removeVideo();
  }

  getGazePoint(): GazePoint {
    if (this.useFallback) {
      return this.fallback.getGazePoint();
    }
    return this.point;
  }
}
